import React, { useEffect, useState } from 'react';
import { Table } from 'react-bootstrap';
import apiDefinitions from '../../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../../core/permissons/CheckPermissons';
import { showError, showSuccessAlert } from '../../../../../core/alert/sweetAlertConfig';
import GateManagementLayout from '../GateManagementLayout';
import { RelayDeviceModal, DeleteRelayModal, ViewRelayModal } from './RelayModals';

interface RelayDevice {
    id: number;
    name: string;
    deviceId: string;
    macAddress?: string;
    status: string;
    createdAt?: string;
}

const RelayDevices: React.FC = () => {
    const [relays, setRelays] = useState<RelayDevice[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedRelay, setSelectedRelay] = useState<RelayDevice | null>(null);
    
    const [formData, setFormData] = useState({
        name: '',
        deviceId: '',
        macAddress: '',
        status: 'ACTIVE'
    });

    const canManage = checkPermission('ROLE_MANAGE_RELAY_DEVICE');

    useEffect(() => {
        fetchRelays();
    }, []);

    const fetchRelays = async () => {
        setLoading(true);
        try {
            const response = await apiDefinitions.getRelayDevices(0, 50, {});
            if (response.data.status === 'S1000') {
                setRelays(response.data.data || []);
            } else {
                showError(response.data.statusDescription || 'Failed to fetch relay devices');
            }
        } catch (error: any) {
            showError('Error fetching relays: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const openAddModal = () => {
        setIsEdit(false);
        setFormData({ name: '', deviceId: '', macAddress: '', status: 'ACTIVE' });
        setShowModal(true);
    };

    const openEditModal = (relay: RelayDevice) => {
        setIsEdit(true);
        setSelectedRelay(relay);
        setFormData({
            name: relay.name,
            deviceId: relay.deviceId,
            macAddress: relay.macAddress || '',
            status: relay.status
        });
        setShowModal(true);
    };

    const openDeleteModal = (relay: RelayDevice) => {
        setSelectedRelay(relay);
        setShowDeleteModal(true);
    };

    const openViewModal = (relay: RelayDevice) => {
        setSelectedRelay(relay);
        setShowViewModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (isEdit && selectedRelay) {
                const response = await apiDefinitions.updateRelayDevice({ ...formData, id: selectedRelay.id });
                if (response.data.status === 'S1000') {
                    showSuccessAlert('Relay device updated successfully');
                    setShowModal(false);
                    fetchRelays();
                } else {
                    showError(response.data.statusDescription || 'Failed to update relay');
                }
            } else {
                const response = await apiDefinitions.createRelayDevice(formData);
                if (response.data.status === 'S1000') {
                    showSuccessAlert('Relay device created successfully');
                    setShowModal(false);
                    fetchRelays();
                } else {
                    showError(response.data.statusDescription || 'Failed to create relay');
                }
            }
        } catch (error: any) {
            showError('Error saving relay: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedRelay) return;
        setLoading(true);
        try {
            const response = await apiDefinitions.deleteRelayDevice(selectedRelay.id);
            if (response.data.status === 'S1000') {
                showSuccessAlert('Relay device deleted successfully');
                setShowDeleteModal(false);
                fetchRelays();
            } else {
                showError(response.data.statusDescription || 'Failed to delete relay');
            }
        } catch (error: any) {
            showError('Error deleting relay: ' + (error.response?.data?.statusDescription || error.message));
        } finally {
            setLoading(false);
        }
    };

    const filteredRelays = relays.filter(r => 
        (r.name || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
        (r.deviceId || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <GateManagementLayout activeTab="relays">
            <div className="card mt-2">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">Relay Device List</h5>
                    {canManage && (
                        <button className="btn btn-primary btn-sm" onClick={openAddModal}>
                            <i className="ti ti-plus me-1"></i> Add Relay
                        </button>
                    )}
                </div>
                <div className="card-body">
                    <div className="mb-3">
                        <input
                            type="text"
                            className="form-control w-25"
                            placeholder="Search by name or device ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="table-responsive">
                        <Table className="table table-borderless table-striped">
                            <thead className="table-light">
                                <tr>
                                    <th>Name</th>
                                    <th>Device ID (eWeLink)</th>
                                    <th>MAC Address</th>
                                    <th>Status</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && filteredRelays.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center">Loading...</td></tr>
                                ) : filteredRelays.length === 0 ? (
                                    <tr><td colSpan={5} className="text-center">No relay devices found</td></tr>
                                ) : (
                                    filteredRelays.map(relay => (
                                        <tr key={relay.id}>
                                            <td className="fw-medium text-dark">{relay.name}</td>
                                            <td><code>{relay.deviceId}</code></td>
                                            <td>{relay.macAddress || 'N/A'}</td>
                                            <td>
                                                <span className={`badge bg-${relay.status === 'ACTIVE' ? 'success' : 'danger'}-subtle text-${relay.status === 'ACTIVE' ? 'success' : 'danger'}`}>
                                                    {relay.status}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <div className="hstack gap-2 justify-content-end">
                                                    <button 
                                                        className="btn btn-icon btn-sm btn-soft-secondary rounded-pill" 
                                                        title="View"
                                                        onClick={() => openViewModal(relay)}
                                                    >
                                                        <i className="feather-eye" />
                                                    </button>
                                                    {canManage && (
                                                        <>
                                                            <button 
                                                                className="btn btn-icon btn-sm btn-soft-primary rounded-pill" 
                                                                title="Edit"
                                                                onClick={() => openEditModal(relay)}
                                                            >
                                                                <i className="feather-edit" />
                                                            </button>
                                                            <button 
                                                                className="btn btn-icon btn-sm btn-soft-danger rounded-pill" 
                                                                title="Delete"
                                                                onClick={() => openDeleteModal(relay)}
                                                            >
                                                                <i className="feather-trash-2" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </div>
                </div>
            </div>

            <RelayDeviceModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSubmit={handleSubmit}
                formData={formData}
                handleInputChange={handleInputChange}
                loading={loading}
                isEdit={isEdit}
            />

            <DeleteRelayModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                deviceName={selectedRelay?.name || ''}
                loading={loading}
            />

            <ViewRelayModal
                show={showViewModal}
                onHide={() => setShowViewModal(false)}
                relay={selectedRelay}
            />
        </GateManagementLayout>
    );
};

export default RelayDevices;
